import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

export const TOKEN_KEY = "finakis_token";

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/login")) {
      localStorage.removeItem(TOKEN_KEY);
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    return Promise.reject(error);
  }
);

export const api = {
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  changePassword: (current_password, new_password) => client.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  generateRecoveryCode: () => client.post("/auth/recovery-code").then((r) => r.data),
  forgotPassword: (email, method) => client.post("/auth/forgot-password", { email, method }).then((r) => r.data),
  resetPassword: (token, new_password) => client.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  resetWithCode: (email, code, new_password) => client.post("/auth/reset-with-code", { email, code, new_password }).then((r) => r.data),
  contactBranches: (id) => client.get(`/contacts/${id}/branches`).then((r) => r.data),
  addBranch: (id, d) => client.post(`/contacts/${id}/branches`, d).then((r) => r.data),
  deleteBranch: (id) => client.delete(`/branches/${id}`),
  updateBranch: (id, d) => client.patch(`/branches/${id}`, d).then((r) => r.data),
  updateStockProduct: (id, d) => client.patch(`/stock/${id}/product`, d).then((r) => r.data),
  summary: () => client.get("/dashboard/summary").then((r) => r.data),
  accounts: () => client.get("/accounts").then((r) => r.data),
  accountStatement: (id, limit = 60) => client.get(`/accounts/${id}/statement`, { params: { limit } }).then((r) => r.data),
  createAccount: (d) => client.post("/accounts", d).then((r) => r.data),
  updateAccount: (id, d) => client.put(`/accounts/${id}`, d).then((r) => r.data),
  deleteAccount: (id) => client.delete(`/accounts/${id}`),
  contacts: () => client.get("/contacts").then((r) => r.data),
  createContact: (d) => client.post("/contacts", d).then((r) => r.data),
  updateContact: (id, d) => client.put(`/contacts/${id}`, d).then((r) => r.data),
  deleteContact: (id) => client.delete(`/contacts/${id}`),
  adjustContactBalance: (id, balance) => client.patch(`/contacts/${id}/balance`, { balance }).then((r) => r.data),
  confirmContact: (id) => client.post(`/contacts/${id}/confirm`).then((r) => r.data),
  collectionsSummary: () => client.get("/contacts/collections-summary").then((r) => r.data),
  contactCollections: (id) => client.get(`/contacts/${id}/collections`).then((r) => r.data),
  contactNotes: (id) => client.get(`/contacts/${id}/notes`).then((r) => r.data),
  addContactNote: (id, text) => client.post(`/contacts/${id}/notes`, { text }).then((r) => r.data),
  deleteNote: (id) => client.delete(`/notes/${id}`),
  noteCounts: () => client.get(`/notes/counts`).then((r) => r.data),
  transactions: (params = {}) => client.get("/transactions", { params }).then((r) => r.data),
  createTransaction: (d) => client.post("/transactions", d).then((r) => r.data),
  deleteTransaction: (id) => client.delete(`/transactions/${id}`),
  payments: () => client.get("/payments").then((r) => r.data),
  bankInflows: () => client.get("/payments/bank-inflows").then((r) => r.data),
  assignBankContact: (txId, body) => client.post(`/payments/assign-contact/${txId}`, body).then((r) => r.data),
  confirmSuggestion: (txId) => client.post(`/payments/confirm-suggestion/${txId}`).then((r) => r.data),
  rejectSuggestion: (txId) => client.post(`/payments/reject-suggestion/${txId}`).then((r) => r.data),
  reprocessUnmatched: () => client.post(`/payments/reprocess-unmatched`).then((r) => r.data),
  createPayment: (d) => client.post("/payments", d).then((r) => r.data),
  markPaid: (id) => client.post(`/payments/${id}/mark-paid`).then((r) => r.data),
  deletePayment: (id) => client.delete(`/payments/${id}`),
  aiInsights: (question) => client.post("/ai/insights", { question }).then((r) => r.data),
  stock: (params = {}) => client.get("/stock", { params }).then((r) => r.data),
  stockAlerts: () => client.get("/stock/alerts").then((r) => r.data),
  stockMargins: (search) => client.get("/stock/margins", { params: search ? { search } : {} }).then((r) => r.data),
  vatReport: () => client.get("/reports/vat").then((r) => r.data),
  assistantHistory: (sessionId) => client.get(`/assistant/history/${sessionId}`).then((r) => r.data),
  adjustStock: (id, quantity) => client.patch(`/stock/${id}`, { quantity }).then((r) => r.data),
  confirmStock: (id) => client.post(`/stock/${id}/confirm`).then((r) => r.data),
  updateStockCode: (id, code) => client.patch(`/stock/${id}/code`, { code }).then((r) => r.data),
  updateStockMeta: (id, d) => client.patch(`/stock/${id}/meta`, d).then((r) => r.data),
  orders: (params) => client.get("/orders", { params }).then((r) => r.data),
  getOrder: (id) => client.get(`/orders/${id}`).then((r) => r.data),
  createOrder: (d) => client.post("/orders", d).then((r) => r.data),
  updateOrderStatus: (id, status) => client.patch(`/orders/${id}/status`, { status }).then((r) => r.data),
  deleteOrder: (id) => client.delete(`/orders/${id}`),
  palletGroups: () => client.get("/pallet-groups").then((r) => r.data),
  listCosts: (search, skip = 0, limit = 50, category) => client.get("/costs", { params: { search, skip, limit, category } }).then((r) => r.data),
  getCost: (id) => client.get(`/costs/${id}`).then((r) => r.data),
  saveCost: (id, cfg) => client.put(`/costs/${id}`, cfg).then((r) => r.data),
  dailySummary: (ai = false) => client.get("/assistant/daily-summary", { params: { ai } }).then((r) => r.data),
  downloadExport: async (path, fallbackName) => {
    const res = await client.get(path, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const name = m ? m[1] : fallbackName;
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  setThreshold: (threshold) => client.put("/settings/stock-threshold", { threshold }).then((r) => r.data),
  fx: (refresh = false) => client.get("/fx", { params: { refresh } }).then((r) => r.data),
  importLogs: () => client.get("/import/logs").then((r) => r.data),
  importInvoices: (formData) => client.post("/import/invoices", formData).then((r) => r.data),
  importBank: (formData) => client.post("/import/bank", formData).then((r) => r.data),
  permissionsList: () => client.get("/permissions").then((r) => r.data),
  listUsers: () => client.get("/users").then((r) => r.data),
  createUser: (d) => client.post("/users", d).then((r) => r.data),
  updateUser: (id, d) => client.put(`/users/${id}`, d).then((r) => r.data),
  deleteUser: (id) => client.delete(`/users/${id}`),
  resetUserPassword: (id, new_password) => client.post(`/users/${id}/reset-password`, { new_password }).then((r) => r.data),
  auditLogs: (params = {}) => client.get("/audit-logs", { params }).then((r) => r.data),
  rebuildBalances: () => client.post("/admin/rebuild-balances").then((r) => r.data),
  getPreferences: () => client.get("/settings/preferences").then((r) => r.data),
  setPreferences: (d) => client.put("/settings/preferences", d).then((r) => r.data),
  updateOrder: (id, d) => client.put(`/orders/${id}`, d).then((r) => r.data),
  backupDb: () => api.downloadExport("/admin/backup", `arslan-yedek-${new Date().toISOString().slice(0, 10)}.json`),
  restoreDb: (formData) => client.post("/admin/restore", formData).then((r) => r.data),
};
